import React from 'react'
import { Feed, HomeScreenWidget, HomeScreenWidgetType, Page, Project, Ref } from '~/models'
import { dataStore, projectStore, SubmitResult } from '~/stores'
import { observer } from '~/ui/component'
import { FormDialog, FormModel, translateFormErrorPaths } from '~/ui/form'
import { useResourceTranslation } from '~/ui/resources'
import { makeObservable, observable } from 'mobx'
import HomeScreenMenuPageWidgetFields from './HomeScreenMenuPageWidgetFields'
import HomeScreenFeedWidgetFields from './HomeScreenFeedWidgetFields'

export interface Props {
  requestClose: () => any
  index:        number | null
  type:         HomeScreenWidgetType | null
  open:         boolean
}

const HomeScreenWidgetForm = observer('HomeScreenWidgetForm', (props: Props) => {

  const {index, type, open, requestClose} = props
  const project = projectStore.project!

  const {t} = useResourceTranslation('projects')

  const formModel = React.useMemo(
    () => index === null && type == null ? null : new HomeScreenWidgetFormModel(project, index, type),
    [index, project, type],
  )

  //------
  // Rendering

  function render() {
    if (formModel == null) { return null }
    return (
      <FormDialog
        requestClose={requestClose}
        title={t('home-screen.widgets.configure')}
        open={open}
        model={formModel}
        children={renderFields()}
      />
    )
  }

  function renderFields() {
    if (formModel == null) { return null }
    switch (formModel.type) {
      case 'menu-page':
        return <HomeScreenMenuPageWidgetFields/>
      case 'feed':
        return <HomeScreenFeedWidgetFields/>
      default:
        return null
    }
  }

  return render()

})

export default HomeScreenWidgetForm

class HomeScreenWidgetFormModel implements FormModel<HomeScreenWidget> {

  constructor(
    private project: Project,
    private index:   number | null,
    type:            HomeScreenWidgetType | null,
  ) {
    if (index === null && type == null) {
      throw new Error("Widget form requires either an index for an existing widget to edit, or a type for a new widget to create")
    }
    if (type != null) {
      this.type = type
    }
    if (index != null) {
      const widget = project.homeScreen.widgets[index]
      this.type = widget.type!
      Object.assign(this, widget?.options ?? {})
    }

    makeObservable(this)
  }

  @observable
  public type!: HomeScreenWidgetType

  // Menu page widget
  @observable
  public page?: Ref<Page>

  // Feed widget
  @observable
  public feed?: Ref<Feed>

  public async submit(): Promise<SubmitResult | undefined> {
    const widgets = this.deriveWidgets()
    const result = await dataStore.update(Project, this.project.id, {
      homeScreen: {...this.project.homeScreen, widgets},
    })

    return translateFormErrorPaths(result, path => path.replace(/^homeScreen.widgets\.\d+\./, ''))
  }

  private deriveWidgets() {
    if (this.index === null) {
      return [
        ...this.project.homeScreen.widgets,
        this.buildWidget(),
      ]
    } else {
      return (this.project.homeScreen.widgets).map((widget, index) => {
        if (index === this.index) {
          return {...widget, ...this.buildWidget()}
        } else {
          return widget
        }
      })
    }
  }

  private buildWidget() {
    const result: Record<string, any> = {
      type:    this.type,
      options: {},
    }
    if (this.type === 'menu-page') {
      result.options.page = this.page
    }
    if (this.type === 'feed') {
      result.options.feed = this.feed
    }
    return result
  }
}